import {
  virtualFolderNames,
  virtualFoldersById,
  DEFAULT_FOLDER_ID,
} from "./constants";
import { toTitleCase } from "./toTitleCase";

export interface VirtualFolderNameOptions {
  /** User information for personalizing folder names */
  user?: {
    firstName?: string | null;
    lastName?: string | null;
  };
  /** Member ID when viewing another user's folder */
  memberId?: string | null;
  /** Member information for personalizing folder names */
  members?: Record<string, { fullName: string }>;
}

/**
 * Gets the display name for a virtual folder in title case
 *
 * Accepts either:
 * - Virtual folder names: "personal", "trash", "pending", etc.
 * - Virtual folder IDs: "0", "-1", "-3", etc.
 *
 * @param folderIdOrName - The virtual folder ID or name
 * @param options - Optional context for personalizing folder names
 * @returns The folder display name in title case, or "Folder" if not found
 *
 * @example
 * ```ts
 * getVirtualFolderName("personal") // "Personal"
 * getVirtualFolderName("trash") // "Trash"
 * getVirtualFolderName("0") // "Personal"
 * getVirtualFolderName("-1") // "Trash"
 * getVirtualFolderName("0", { user: { firstName: "John", lastName: "Doe" } }) // "John Doe (Personal)"
 * getVirtualFolderName("0", { user: { firstName: "John", lastName: null } }) // "John (Personal)"
 * getVirtualFolderName("unknown") // "Folder"
 * ```
 */
export function getVirtualFolderName(
  folderIdOrName: string,
  options?: VirtualFolderNameOptions,
): string {
  // Determine the virtual folder ID
  let virtualId = folderIdOrName;

  // First check if it's a virtual folder name that maps to an ID
  if (virtualFolderNames[folderIdOrName]) {
    virtualId = virtualFolderNames[folderIdOrName];
  }

  // Handle personal folder with user context
  if (virtualId === DEFAULT_FOLDER_ID && options) {
    if (options.memberId && options.members?.[options.memberId]) {
      const member = options.members[options.memberId];
      return `${member.fullName} (Personal)`;
    }
    if (options.user) {
      const firstName = options.user.firstName?.trim?.();
      const lastName = options.user.lastName?.trim?.();

      if (firstName && lastName) {
        return `${firstName} ${lastName} (Personal)`;
      } else if (firstName) {
        return `${firstName} (Personal)`;
      } else if (lastName) {
        return `${lastName} (Personal)`;
      }
    }
  }

  // Use standard virtual folder name
  const folderInfo = virtualFoldersById[virtualId];
  if (folderInfo) {
    return toTitleCase(folderInfo.name);
  }

  // Fallback for unknown virtual folders
  return "Folder";
}
